import React, { useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import moment from 'moment';
import { theme } from 'theme';

import { Card, CardHeader, CardSection } from 'components/Card';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';

import { formatNumber, formatUSD, formatUSDAbbreviated } from 'utils/formatting';

import { InventoryDashboardAreaChart } from './InventoryDashboardAreaChart';
import { CardLoader } from './shared/CardLoader';
import { DashboardUnit, DashboardUnitSelect } from './shared/DashboardUnitSelect';

interface ChartPoint {
  date: string;
  total: number;
  normal: number;
  overstock: number;
  deadstock: number;
}

type SeriesKey = Exclude<keyof ChartPoint, 'date'>;

const SERIES: { key: SeriesKey; title: string; themeChartColor: keyof typeof theme.colors.chart }[] = [
  { key: 'total', title: 'Total', themeChartColor: 'sunriseYellow' },
  { key: 'normal', title: 'Normal', themeChartColor: 'aloeGreen' },
  { key: 'overstock', title: 'Overstock', themeChartColor: 'sunsetPurple' },
  { key: 'deadstock', title: 'Deadstock', themeChartColor: 'malibuOrange' },
];

export function InventorySnapshotsChart() {
  const [displayUnit, setDisplayUnit] = useState<DashboardUnit>('value');

  const { data: snapshotData, isLoading } = useCoreApi(schemas.inventory.getInventoryDashboardSnapshots, {
    queryParams: {},
  });

  const formatVolume = displayUnit === 'volume';

  let lastDate: moment.Moment | null = null;
  const chartData: ChartPoint[] = (snapshotData?.items ?? []).reduce<ChartPoint[]>((acc, r) => {
    const date = moment(r.date);

    // Remove dates less than 7 days apart
    if (lastDate && date.diff(lastDate, 'days') < 7) {
      return acc;
    }

    lastDate = date;
    acc.push({
      date: date.format('M/D/YY'),
      total: formatVolume ? r.totalVolume : r.totalValue,
      normal: formatVolume ? r.normalVolume : r.normalValue,
      overstock: formatVolume ? r.overstockVolume : r.overstockValue,
      deadstock: formatVolume ? r.deadstockVolume : r.deadstockValue,
    });
    return acc;
  }, []);

  return (
    <>
      <div
        className={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        `}
      >
        <Typography type="h3" style={{ fontWeight: 600 }}>
          Inventory Snapshots
        </Typography>
        <DashboardUnitSelect state={displayUnit} onChange={setDisplayUnit} />
      </div>
      {isLoading ? (
        <CardLoader height={200} />
      ) : (
        <div
          className={css`
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 18px;
            @media (max-width: 480px) {
              grid-template-columns: minmax(0, 1fr);
            }
          `}
        >
          {SERIES.map((series) => (
            <Card key={series.key}>
              <CardHeader title={series.title} />
              <CardSection>
                <InventoryDashboardAreaChart
                  data={chartData.map((point) => ({
                    date: point.date,
                    value: point[series.key],
                    units: formatVolume ? '' : '$',
                  }))}
                  containerHeights={{ minHeight: 180, maxHeight: 240 }}
                  themeChartColor={series.themeChartColor}
                  size="small"
                  yAxisProps={{
                    dataKey: 'value',
                    tickFormatter: (value) =>
                      formatVolume
                        ? formatNumber(value, 0, undefined, { notation: 'compact', useGrouping: true })
                        : formatUSDAbbreviated(value, 0),
                  }}
                  tooltip={{
                    cursor: false,
                    formatter: (value) => (formatVolume ? formatNumber(Number(value), 0) : formatUSD(Number(value))),
                  }}
                  tooltipLabelFormatter={(d) => moment(d, 'M/D/YY').format('MMM D, YYYY')}
                />
              </CardSection>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
